import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { endpoints } from "@/config/endpoints";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, X, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/auth.service";
import { useNavigate } from "react-router-dom";
import AddUserModal from "@/components/AddUserModal";
import { Checkbox } from "@/components/ui/checkbox";

interface User {
  id: string;
  _id?: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
  status: "active" | "inactive" | "suspended";
  suspensionReason?: string;
  suspensionEndDate?: string;
}

const AVAILABLE_ROLES = [
  "admin",
  "site_manager",
  "overall_site_manager",
  "quantity_surveyor",
  "procurement",
  "director",
];

const Users = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [updateForm, setUpdateForm] = useState<Partial<User>>({});
  const [newRole, setNewRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const { token } = authService.getStoredAuth();

  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await fetch(`${endpoints.auth.users}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          try {
            // Try to refresh the token
            const newToken = await authService.refreshToken();
            // Retry the request with new token
            const retryResponse = await fetch(`${endpoints.auth.users}`, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to fetch users after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            // If refresh fails, logout
            authService.logout();
            // navigate("/login");
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to fetch users");
      }
      return response.json();
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (updatedUser: User) => {
      const userId = updatedUser._id || updatedUser._id;
      const token = localStorage.getItem("auth_token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${endpoints.auth.user(userId)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedUser),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User updated successfully");
      setSelectedUser(null);
      setUpdateForm({});
      setNewRole("");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update user");
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(`${endpoints.auth.user(userId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          try {
            // Try to refresh the token
            const newToken = await authService.refreshToken();
            // Retry the request with new token
            const retryResponse = await fetch(
              `${endpoints.auth.user(userId)}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              }
            );
            if (!retryResponse.ok) {
              throw new Error("Failed to delete user after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            // If refresh fails, logout
            authService.logout();
            // navigate("/login");
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to delete user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete user");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      const response = await fetch(`${endpoints.auth.bulkDeleteUsers}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userIds }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(
              `${endpoints.auth.bulkDeleteUsers}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${newToken}`,
                },
                body: JSON.stringify({ userIds }),
              }
            );
            if (!retryResponse.ok) {
              throw new Error("Failed to delete users after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to delete users");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Users deleted successfully");
      setSelectedUsers([]);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete users");
    },
  });

  const suspendUserMutation = useMutation({
    mutationFn: async ({
      userId,
      reason,
      endDate,
    }: {
      userId: string;
      reason: string;
      endDate?: string;
    }) => {
      const response = await fetch(endpoints.users.suspend(userId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason, endDate }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(endpoints.users.suspend(userId), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              body: JSON.stringify({ reason, endDate }),
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to suspend user after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to suspend user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User suspended successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to suspend user");
    },
  });

  const unsuspendUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetch(endpoints.users.unsuspend(userId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(
              endpoints.users.unsuspend(userId),
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              }
            );
            if (!retryResponse.ok) {
              throw new Error("Failed to unsuspend user after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to unsuspend user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("User unsuspended successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to unsuspend user");
    },
  });

  const handleDelete = (userId: string) => {
    deleteUserMutation.mutate(userId);
  };

  const handleUpdate = (user: User) => {
    setSelectedUser(user);
    setUpdateForm(user);
  };

  const handleUpdateSubmit = () => {
    if (selectedUser) {
      updateUserMutation.mutate({ ...selectedUser, ...updateForm });
    }
  };

  const handleAddRole = () => {
    if (newRole && !updateForm.roles?.includes(newRole)) {
      setUpdateForm({
        ...updateForm,
        roles: [...(updateForm.roles || []), newRole],
      });
      setNewRole("");
    }
  };

  const handleRemoveRole = (roleToRemove: string) => {
    setUpdateForm({
      ...updateForm,
      roles: updateForm.roles?.filter((role) => role !== roleToRemove) || [],
    });
  };

  // Filter users based on search term
  const filteredUsers = users?.filter((user: User) => {
    // Skip users with supplier role
    if (user.roles.includes("supplier")) {
      return false;
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.phone.toLowerCase().includes(searchLower) ||
      user.roles.some((role) => role.toLowerCase().includes(searchLower))
    );
  });

  // Calculate pagination with filtered users
  const totalPages = Math.ceil((filteredUsers?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers?.slice(startIndex, endIndex);

  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select users to delete");
      return;
    }

    bulkDeleteMutation.mutate(selectedUsers);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(currentUsers?.map((user) => user._id) || []);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Users">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    toast.error("Failed to load users");
    return (
      <DashboardLayout title="Users">
        <div className="text-center text-red-500">Error loading users</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Users</CardTitle>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[300px]"
              />
            </div>
            {selectedUsers.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={bulkDeleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedUsers.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete{" "}
                      {selectedUsers.length} user account(s).
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleBulkDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button
              variant="default"
              size="sm"
              className="bg-orange-500 hover:bg-orange-600"
              onClick={() => setIsAddUserModalOpen(true)}
            >
              <Plus size={16} className="mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedUsers.length === currentUsers?.length}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentUsers?.map((user: User) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedUsers.includes(user._id)}
                      onCheckedChange={(checked) =>
                        handleSelectUser(user._id, checked as boolean)
                      }
                    />
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.roles?.map((role) => (
                        <Badge key={role} variant="secondary">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        user.status === "active"
                          ? "default"
                          : user.status === "suspended"
                          ? "destructive"
                          : "destructive"
                      }
                    >
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdate(user)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Update
                      </Button>
                      {user.status === "suspended" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-green-500 hover:bg-green-600 text-white"
                          onClick={() => unsuspendUserMutation.mutate(user._id)}
                          disabled={unsuspendUserMutation.isPending}
                        >
                          Unsuspend
                        </Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-yellow-500 hover:bg-yellow-600 text-white"
                            >
                              Suspend
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Suspend User</AlertDialogTitle>
                              <AlertDialogDescription>
                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="reason">
                                      Reason for Suspension
                                    </Label>
                                    <Input
                                      id="reason"
                                      placeholder="Enter reason for suspension"
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="endDate">
                                      End Date (Optional)
                                    </Label>
                                    <Input
                                      id="endDate"
                                      type="date"
                                      className="mt-1"
                                    />
                                  </div>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => {
                                  const reason = (
                                    document.getElementById(
                                      "reason"
                                    ) as HTMLInputElement
                                  ).value;
                                  const endDate = (
                                    document.getElementById(
                                      "endDate"
                                    ) as HTMLInputElement
                                  ).value;
                                  if (!reason) {
                                    toast.error(
                                      "Please provide a reason for suspension"
                                    );
                                    return;
                                  }
                                  suspendUserMutation.mutate({
                                    userId: user._id,
                                    reason,
                                    endDate,
                                  });
                                }}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                              >
                                Suspend
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={deleteUserMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will
                              permanently delete the user account.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(user._id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, filteredUsers?.length || 0)} of{" "}
              {filteredUsers?.length || 0} users
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUser?._id ? "Update User" : "Add New User"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={updateForm.name || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                value={updateForm.email || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, email: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={updateForm.phone || ""}
                onChange={(e) =>
                  setUpdateForm({ ...updateForm, phone: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={updateForm.status || ""}
                onValueChange={(value) =>
                  setUpdateForm({
                    ...updateForm,
                    status: value as "active" | "inactive" | "suspended",
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Roles</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {updateForm.roles?.map((role) => (
                  <Badge
                    key={role}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {role}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveRole(role)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role to add" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_ROLES.map((role) => (
                      <SelectItem
                        key={role}
                        value={role}
                        disabled={updateForm.roles?.includes(role)}
                      >
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  className="bg-orange-500 text-primary-foreground hover:bg-primary/90"
                  onClick={handleAddRole}
                  disabled={!newRole}
                >
                  Add Role
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedUser(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSubmit}
              disabled={updateUserMutation.isPending}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["users"] });
        }}
      />
    </DashboardLayout>
  );
};

export default Users;
